'use client';

import type { INotification } from 'src/types/notification';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import { useTranslation } from 'react-i18next';

type NotificationItemProps = {
  notification: INotification;
  onClick?: () => void;
};

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const { t } = useTranslation('notifications');
  const isUnread = !notification.readAt;

  const handleClick = () => {
    onClick?.();
  };

  const formatTime = (date: string | Date) => {
    const now = new Date();
    const notificationDate = new Date(date);

    if (notificationDate.toDateString() === now.toDateString()) {
      return notificationDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }

    return notificationDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    });
  };

  return (
    <ButtonBase
      onClick={handleClick}
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        py: 2,
        px: 2.5,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        '&:hover': {
          bgcolor: 'action.hover',
        },
        ...(isUnread && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <Stack spacing={0.5}>
        {/* Title with unread indicator */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              fontWeight: 400,
              fontSize: 14,
              lineHeight: 1.57,
              color: 'text.primary',
            }}
          >
            {notification.summary || (notification.entityType === 'Task' && typeof notification.metadata === 'object' && notification.metadata && (notification.metadata as any).object_task && (notification.metadata as any).object_task?.name
              ? `Task "${(notification.metadata as any).object_task.name}" ${notification.action?.toLowerCase()}d`
              : t('notifications.title'))}
          </Typography>
          
          {isUnread && (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'info.main',
                flexShrink: 0,
              }}
            />
          )}
        </Stack>

        {/* Timestamp */}
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography
            variant="caption"
            sx={{
              fontSize: 12,
              color: 'text.secondary',
            }}
          >
            {formatTime(notification.createdAt)}
          </Typography>
          
          <Box
            sx={{
              width: 2,
              height: 2,
              borderRadius: '50%',
              bgcolor: 'text.secondary',
            }}
          />
          
          <Typography
            variant="caption"
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              textTransform: 'capitalize',
            }}
          >
            {t(`notifications.actions.${notification.action?.toLowerCase()}`)}
          </Typography>
        </Stack>
      </Stack>
    </ButtonBase>
  );
}